import { useState } from 'react'
import { EstadoError } from '../componentes/EstadoError'
import { EstadoVacio } from '../componentes/EstadoVacio'
import { fechaCorta, hoyIso } from '../componentes/formato'
import { AvisoError, CabeceraPagina, Campo, CLASE_BOTON, CLASE_BOTON_PRIMARIO, CLASE_INPUT, Insignia } from '../componentes/formulario'
import { IconoAbrir, IconoActualizar, IconoDescargar, IconoUbicacion } from '../componentes/iconos'
import { SkeletonList } from '../componentes/Skeleton'
import type { GrupoRuta, ParadaRuta } from '../api/modelos'
import { useCalcularRuta, useParteRuta, usePdfRuta } from '../hooks/useRutaDia'
import { COLORES_DIA } from './agendamiento/coloresDia'
import type { DiaRuta } from '../hooks/useFiltrosAgendamiento'
import { BarraPaginacion } from '../componentes/BarraPaginacion'
import { usePaginacionLocal } from '../componentes/paginacion'

function nombreGrupo(etiqueta: string): string {
  const color = COLORES_DIA[etiqueta as DiaRuta]
  if (color) return `Ruta ${etiqueta.toUpperCase()} · ${color.etiqueta}`
  return etiqueta === 'sin_ruta' || etiqueta === '' ? 'Sin ruta' : `Ruta ${etiqueta}`
}

/** El backend lo marca (`sinUbicacion`); un parte sin el campo se deduce de las coordenadas. */
function sinUbicacion(p: ParadaRuta): boolean {
  return p.sinUbicacion ?? (p.latitud === null || p.longitud === null)
}

function Grupo({ grupo, fecha }: { grupo: GrupoRuta; fecha: string }) {
  const pdf = usePdfRuta(fecha)
  const color = COLORES_DIA[grupo.etiqueta as DiaRuta]
  const titulo = nombreGrupo(grupo.etiqueta)
  const pag = usePaginacionLocal(grupo.paradas)
  return (
    <section aria-label={titulo} className="flex flex-col gap-3 rounded-xl border border-borde bg-superficie p-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ring-1 ${color ? `${color.fondo} ${color.tinta} ${color.anillo}` : 'bg-elevado text-tinta-2 ring-borde'}`}>
            {titulo}
          </span>
          <span className="text-sm text-tinta-apagada">
            {grupo.paradas.length === 1 ? '1 parada' : `${grupo.paradas.length} paradas`}
          </span>
          {grupo.fusionadas?.length ? (
            <span className="text-xs text-tinta-apagada">Incluye también: {grupo.fusionadas.map((r) => r.toUpperCase()).join(', ')}</span>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2">
          {grupo.mapsUrl ? (
            <a href={grupo.mapsUrl} target="_blank" rel="noopener noreferrer" className={CLASE_BOTON}>
              <IconoAbrir aria-hidden="true" className="size-4" />
              Abrir en Maps
            </a>
          ) : null}
          <button
            type="button"
            className={CLASE_BOTON}
            disabled={pdf.isPending}
            onClick={() => pdf.mutate({ etiqueta: grupo.etiqueta })}
            aria-label={`PDF de ${titulo}`}
          >
            <IconoDescargar aria-hidden="true" className="size-4" />
            {pdf.isPending ? 'Preparando' : 'PDF'}
          </button>
        </div>
      </header>
      <ol className="flex flex-col divide-y divide-borde-suave">
        {pag.visibles.map((p) => (
          <li key={`${p.albaranId}-${p.orden}`} className="flex items-start gap-3 py-2">
            <span className="grid size-7 shrink-0 place-items-center rounded-full bg-elevado text-xs font-bold tabular-nums text-tinta">
              {p.orden}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-tinta">{p.nombre}</p>
              <p className="text-xs text-tinta-apagada">
                {p.direccion || 'Sin dirección'} · {p.numero}
                {p.telefono ? ` · ${p.telefono}` : ''}
              </p>
              {p.notaEntrega ? <p className="text-xs italic text-tinta-2">{p.notaEntrega}</p> : null}
            </div>
            {sinUbicacion(p) ? (
              <Insignia tono="atencion">
                <IconoUbicacion aria-hidden="true" className="mr-1 size-3" />
                Sin ubicación
              </Insignia>
            ) : null}
          </li>
        ))}
      </ol>
      <BarraPaginacion pagina={pag.pagina} paginas={pag.paginas} onCambiar={pag.irAPagina} etiqueta={`Paginación de ${titulo}`} />
      <AvisoError error={pdf.error} />
    </section>
  )
}

/**
 * Ruta del día (RF-44): agrupa los albaranes de la fecha por ruta, ordena las
 * paradas desde el punto de salida y da el enlace de Google Maps y el PDF de
 * cada ruta. Calcular otra vez sustituye el parte.
 */
export function RutaDelDia() {
  const [fecha, setFecha] = useState(hoyIso())
  const parte = useParteRuta(fecha)
  const calcular = useCalcularRuta(fecha)
  const grupos = parte.data?.grupos ?? []

  return (
    <div className="flex flex-col gap-4">
      <CabeceraPagina
        titulo="Ruta del día"
        descripcion="Las paradas de cada ruta en orden, con su enlace de Google Maps y el PDF para el repartidor. Las paradas sin coordenadas van al final."
      />
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-48">
          <Campo etiqueta="Fecha de reparto">
            <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className={CLASE_INPUT} />
          </Campo>
        </div>
        <button type="button" className={CLASE_BOTON_PRIMARIO} disabled={calcular.isPending || !fecha} onClick={() => calcular.mutate()}>
          <IconoActualizar aria-hidden="true" className="size-4" />
          {calcular.isPending ? 'Calculando' : parte.data ? 'Volver a calcular' : 'Calcular ruta'}
        </button>
        {parte.data?.origen === 'externo' ? (
          <span className="pb-2 text-xs text-tinta-apagada">Parte recibido de fuera: «Volver a calcular» lo sustituye.</span>
        ) : parte.data?.calculadoEn ? (
          <span className="pb-2 text-xs text-tinta-apagada">
            Calculada el {new Date(parte.data.calculadoEn).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })}
          </span>
        ) : null}
      </div>
      <AvisoError error={calcular.error} />

      {!fecha ? null : parte.isPending ? (
        <SkeletonList />
      ) : parte.isError ? (
        <EstadoError error={parte.error} onReintentar={() => void parte.refetch()} />
      ) : !parte.data ? (
        <EstadoVacio
          titulo={`Sin ruta calculada para el ${fechaCorta(fecha)}`}
          descripcion="Pulsa «Calcular ruta» cuando estén publicados los albaranes del día."
        />
      ) : grupos.length === 0 ? (
        <EstadoVacio titulo="No hay albaranes ese día" descripcion="No hay nada que repartir: no se genera PDF ni correo." />
      ) : (
        <div className="grid gap-4 xl:grid-cols-2">
          {grupos.map((g) => <Grupo key={g.etiqueta} grupo={g} fecha={fecha} />)}
        </div>
      )}
    </div>
  )
}
